// components/stockAdjustment/StockAdjustment.jsx
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createAdjustment, getAdjustments,
  getSingleAdjustment, updateAdjustment, deleteAdjustment
} from '../../redux/features/sales/stockAdjustmentSlice';
import { getProducts } from '../../redux/features/product/productSlice';
import { getProductBatches } from '../../redux/features/product/productBatchSlice';
import { useTable, usePagination, useGlobalFilter, useFilters } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { MdEditSquare, MdDeleteForever } from 'react-icons/md';
import { AiOutlinePlus } from "react-icons/ai";
import ButtonLoading from '../../loader/ButtonLoader';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { Modal } from "bootstrap";
import Select from "react-select";
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ColumnToggle } from './ColumnToggle';
import { convertStock } from '../../redux/features/sales/stockAdjustmentSlice';
import { calcBuyOptions, applyWastage } from '../../utils/ceramicCalculator';
import { getBoxInfo } from '../../utils/boxInfo';
// Import at top
import { ConvertedItems } from './ConvertedItems';
import {
  calcTileToLinearMeter,
  calcLinearMeterConversion,
  getSourceDimensions,
  round
} from '../../utils/conversionCalculator';
import { EthiopianDateCell } from './EthiopianDateCell';
import { SimpleDualDateInput } from '../DualDateInput';



const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;
const formatDate = (d) => d
  ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  : '';

const initialState = {
  product:        null,
  productBatch:   null,
  quantity:       "",
  type:           null,
  reason:         "",
  isRecoverable:  false,
  recoveryNote:   "",
  adjustmentDate: "",
};

const convertInitial = {
  sourceProduct:      null,
  sourceBatch:        null,
  sourceQuantity:     "",
  sourceWidth:        "",   // ✅ user tells us the tile width
  sourceLength:       "",   // ✅ user tells us the tile length
  cuttingSide:        "",   // ✅ which side is being cut (meters)
  targetProduct:      null,
  cuttingWidth:       "",   // ✅ width of each cut strip (e.g. 0.10 for 10cm skirting)
  targetQuantity:     "",   // auto-calculated, editable
  targetSellingPrice: "",
  targetPurchasePrice:"",
  targetSupplier:     "",
  reason:             "",
  adjustmentDate:     "",
};

const typeOptions = [
  { value: "lost",       label: "Lost" },
  { value: "damaged",    label: "Damaged" },
  { value: "expired",    label: "Expired" },
  { value: "correction", label: "Stock Correction" },
  { value: "other",      label: "Other" },
];

export const StockAdjustment = () => {
  useRedirectLogOutUser();
  const dispatch = useDispatch();

  const addModalRef    = useRef(null);
  const editModalRef   = useRef(null);
  const deleteModalRef = useRef(null);
  const convertModalRef = useRef(null);

  const [isLoadingConvert, setIsLoadingConvert] = useState(false);
  const [form, setForm]         = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [convertForm, setConvertForm] = useState(convertInitial);
  const [activeTab, setActiveTab] = useState("adjustments"); // "adjustments" | "conversions"


  const { adjustments, adjustment } = useSelector((state) => state.stockAdjustment);
  const { products }               = useSelector((state) => state.product);
  const { productBatches }         = useSelector((state) => state.productBatch);
  const { userType }               = useSelector((state) => state.auth);
  const isAdmin                    = userType === "admin";

  const productOptions = useMemo(() =>
    products.map(p => ({ value: p._id, label: `${p.name} (${p.sku})` })),
  [products]);

  const batchOptionsFor = (productId) =>
    productBatches
      .filter(b => {
        const bProdId = b.product?._id || b.product;
        return bProdId === productId && b.remainingQuantity > 0;
      })
      .map(b => ({
        value: b._id,
        label: `Batch ${new Date(b.createdAt).toLocaleDateString()} — Stock: ${b.remainingQuantity} — Price: ${b.purchasePrice}`
      }));

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getAdjustments());
    dispatch(getProducts());
    dispatch(getProductBatches());
  }, [dispatch]);

  useEffect(() => {
    if (adjustment && editId) {
      setForm({
        product:       adjustment.product ? { value: adjustment.product._id, label: adjustment.product.name } : null,
        productBatch:  null,
        quantity:      adjustment.quantity      ?? "",
        type:          typeOptions.find(t => t.value === adjustment.type) || null,
        reason:        adjustment.reason        ?? "",
        isRecoverable: adjustment.isRecoverable ?? false,
        recoveryNote:  adjustment.recoveryNote  ?? "",
        adjustmentDate: adjustment.adjustmentDate
          ? new Date(adjustment.adjustmentDate).toISOString().split("T")[0] : "",
      });
    }
  }, [adjustment, editId]);

// ── Source product doc ──────────────────────────────────────
const sourceProductDoc = useMemo(() =>
  products.find(p => p._id === convertForm.sourceProduct?.value),
[products, convertForm.sourceProduct]);



const targetProductDoc = useMemo(() =>
  products.find(p => p._id === convertForm.targetProduct?.value),
[products, convertForm.targetProduct]);

const sourceUnit = sourceProductDoc?.category?.measurementUnit || "pcs";
const targetUnit = targetProductDoc?.category?.measurementUnit || "pcs";

// ── Buy options for source quantity — validates sellable amount ──
const sourceBuyOptions = useMemo(() => {
  if (!convertForm.sourceQuantity || !sourceProductDoc) return null;
  const isBoxed = !!(sourceProductDoc?.itemsPerBox);
  const hasSize = !!(sourceProductDoc?.sizePerItem);
  if (!isBoxed && !hasSize) return null;
  return calcBuyOptions(
    Number(convertForm.sourceQuantity),
    sourceProductDoc,
    sourceUnit
  );
}, [convertForm.sourceQuantity, sourceProductDoc, sourceUnit]);

// ── Box info for the selected source quantity ────────────────
const sourceBoxInfo = useMemo(() => {
  if (!convertForm.sourceQuantity || !sourceProductDoc?.itemsPerBox) return null;
  const productWithUnit = {
    ...sourceProductDoc,
    category: typeof sourceProductDoc.category === "object"
      ? sourceProductDoc.category
      : { measurementUnit: sourceUnit },
  };
  return getBoxInfo(Number(convertForm.sourceQuantity), productWithUnit);
}, [convertForm.sourceQuantity, sourceProductDoc, sourceUnit]);

// ── Source tile m² ──────────────────────────────────────────
const sourceTotalM2 = useMemo(() => {
  if (!convertForm.sourceQuantity || !sourceProductDoc) return 0;
  const qty = Number(convertForm.sourceQuantity);
  const spi = sourceProductDoc?.sizePerItem;
  if (sourceUnit === "m2") return qty;
  if (sourceUnit === "pcs" && spi) return round(qty * spi);
  return 0;
}, [convertForm.sourceQuantity, sourceProductDoc, sourceUnit]);

// ── Available cutting sides from source tile ────────────────
const sourceDimensions = useMemo(() => {
  const w = Number(convertForm.sourceWidth);
  const l = Number(convertForm.sourceLength);
  return getSourceDimensions(sourceProductDoc, w, l);
}, [sourceProductDoc, convertForm.sourceWidth, convertForm.sourceLength]);

// ── Detailed conversion result ──────────────────────────────
const conversionResult = useMemo(() => {
  if (targetUnit !== "linear_meter") return null;
  const w  = Number(convertForm.sourceWidth);
  const l  = Number(convertForm.sourceLength);
  const cs = Number(convertForm.cuttingSide);
  const cw = Number(convertForm.cuttingWidth);
  if (!sourceTotalM2 || !w || !l || !cs || !cw) return null;
  return calcTileToLinearMeter(sourceTotalM2, w, l, cw, cs);
}, [
  sourceTotalM2,
  convertForm.sourceWidth,
  convertForm.sourceLength,
  convertForm.cuttingSide,
  convertForm.cuttingWidth,
  targetUnit,
]);

// ── Auto-calculated target quantity ────────────────────────
const autoConvertedQty = useMemo(() => {
  if (!sourceTotalM2 || !sourceProductDoc || !targetProductDoc) return null;

  if (targetUnit === "linear_meter") {
    return conversionResult?.totalLinearMeters ?? null;
  }

  // m² → pcs
  if (targetUnit === "pcs") {
    const tsp = targetProductDoc?.sizePerItem;
    if (!tsp) return null;
    return +Math.floor(sourceTotalM2 / tsp).toFixed(0);
  }

  // m² → m² (same unit)
  if (targetUnit === "m2") return +sourceTotalM2.toFixed(4);

  return null;
}, [sourceTotalM2, sourceProductDoc, targetProductDoc,
    targetUnit, conversionResult]);

// ── Buy options for the converted quantity — same as sales/batch ──
const targetBuyOptions = useMemo(() => {
  if (!autoConvertedQty || !targetProductDoc) return null;
  const unit = targetUnit;
  const isBoxed = !!(targetProductDoc?.itemsPerBox);
  const hasSize = !!(targetProductDoc?.sizePerItem);
  if (!isBoxed && !hasSize) return null;
  return calcBuyOptions(autoConvertedQty, targetProductDoc, unit);
}, [autoConvertedQty, targetProductDoc, targetUnit]);

// ── Box info for the final selected target quantity ──────────
const targetBoxInfo = useMemo(() => {
  if (!convertForm.targetQuantity || !targetProductDoc?.itemsPerBox) return null;
  const productWithUnit = {
    ...targetProductDoc,
    category: typeof targetProductDoc.category === "object"
      ? targetProductDoc.category
      : { measurementUnit: targetUnit },
  };
  return getBoxInfo(Number(convertForm.targetQuantity), productWithUnit);
}, [convertForm.targetQuantity, targetProductDoc, targetUnit]);

// Auto-fill targetQuantity — use round-up option if available
// Auto-fill targetQuantity from raw conversion result
useEffect(() => {
  if (autoConvertedQty !== null) {
    setConvertForm(prev => ({ ...prev, targetQuantity: autoConvertedQty }));
  }
}, [autoConvertedQty]);

// When source product changes, try to pre-fill width/length from sizePerItem
useEffect(() => {
  if (!sourceProductDoc) return;
  const spi = sourceProductDoc.sizePerItem;
  if (!spi || sourceUnit !== "m2") return;
  // If it looks like a square tile (e.g. 0.36 = 0.6×0.6)
  const side = +Math.sqrt(spi).toFixed(4);
  if (Math.abs(round(side * side) - spi) < 0.0001) {
    // Square tile — pre-fill both
    setConvertForm(prev => ({
      ...prev,
      sourceWidth:  prev.sourceWidth  || String(side),
      sourceLength: prev.sourceLength || String(side),
    }));
  }
}, [sourceProductDoc]);

// ── Only show convertible products ────────────────────────────
// A product is convertible if it has sizePerItem (m² or linear_meter)
const convertibleProductOptions = useMemo(() =>
  products
    .filter(p => p.sizePerItem && p.sizePerItem > 0)
    .map(p => ({
      value: p._id,
      label: `${p.name} (${p.sku}) — ${p.sizePerItem} ${
        p.category?.measurementUnit === "m2" ? "m²" : "m"
      }/pc`,
    })),
[products]);

const openConvertModal = () => {
  setConvertForm(convertInitial);
  document.activeElement?.blur();
  new Modal(convertModalRef.current).show();
};

const handleConvertSubmit = async (e) => {
  e.preventDefault();
  const {
    sourceProduct, sourceBatch, sourceQuantity,
    targetProduct, targetQuantity, targetSellingPrice,
    targetPurchasePrice, targetSupplier, reason, adjustmentDate,
  } = convertForm;

  if (!sourceProduct || !sourceBatch || !sourceQuantity ||
      !targetProduct  || !targetQuantity || !targetSellingPrice) {
    return toast.error("Please fill all required fields");
  }
  if (Number(targetQuantity) <= 0) {
    return toast.error("Converted quantity must be greater than zero");
  }

  setIsLoadingConvert(true);
  await dispatch(convertStock({
    sourceProduct:       sourceProduct.value,
    sourceBatch:         sourceBatch.value,
    sourceQuantity:      Number(sourceQuantity),
    targetProduct:       targetProduct.value,
    targetQuantity:      Number(targetQuantity),
    targetSellingPrice:  Number(targetSellingPrice),
    targetPurchasePrice: Number(targetPurchasePrice) || 0,
    targetSupplier:      targetSupplier || "",
    reason:              reason || `Converted to ${targetProductDoc?.name}`,
    adjustmentDate:      adjustmentDate || null,
  }));
  setIsLoadingConvert(false);
  dispatch(getAdjustments());
  const modalEl = convertModalRef.current;
if (modalEl) {
  const instance = Modal.getInstance(modalEl);
  if (instance) {
    instance.hide();
  } else {
    // fallback — bootstrap may have lost the instance
    const m = new Modal(modalEl);
    m.hide();
  }
}
};

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: "46px", fontSize: "1.5rem" }),
    menu:    (base) => ({ ...base, zIndex: 9999, fontSize: "1.5rem" }),
  };

  // ── Modal openers ──────────────────────────────────────────
  const openAddModal = () => {
    setForm(initialState);
    document.activeElement?.blur();
    new Modal(addModalRef.current).show();
  };

  const openEditModal = (id) => {
    setEditId(id);
    dispatch(getSingleAdjustment(id));
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    document.activeElement?.blur();
    new Modal(deleteModalRef.current).show();
  };

  // ── Add submit ─────────────────────────────────────────────
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!form.product || !form.productBatch || !form.quantity || !form.type || !form.reason) {
      return toast.error("Please fill in all required fields");
    }
    setIsLoading(true);
    await dispatch(createAdjustment({
      product:        form.product.value,
      productBatch:   form.productBatch.value,
      quantity:       Number(form.quantity),
      type:           form.type.value,
      reason:         form.reason,
      isRecoverable:  form.isRecoverable,
      recoveryNote:   form.recoveryNote || null,
      adjustmentDate: form.adjustmentDate || null,
    }));
    setIsLoading(false);
    setForm(initialState);
    // keep modal open for multiple entries
    toast.success("Adjustment recorded — add another or close.");
  };

  // ── Edit submit ────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await dispatch(updateAdjustment({
      id: editId,
      data: {
        reason:        form.reason,
        recoveryNote:  form.recoveryNote || null,
        isRecoverable: form.isRecoverable,
      }
    }));
    Modal.getInstance(editModalRef.current).hide();
  };

  // ── Delete ─────────────────────────────────────────────────
  const handleDelete = () => {
    dispatch(deleteAdjustment(deleteId));
    Modal.getInstance(deleteModalRef.current).hide();
  };

  // ── Shared form fields ─────────────────────────────────────
  const renderFormFields = (isEdit = false) => (
    <div className="row g-3">

      <div className="col-12">
        <label className="fs-4">Product <span className="text-danger">*</span></label>
        <Select
          options={productOptions}
          value={form.product}
          onChange={(v) => setForm(prev => ({ ...prev, product: v || null, productBatch: null }))}
          isDisabled={isEdit}
          placeholder="Select product..."
          isClearable isSearchable
          styles={selectStyles}
        />
      </div>

      {!isEdit && (
        <div className="col-12">
          <label className="fs-4">Batch <span className="text-danger">*</span></label>
          <Select
            options={form.product ? batchOptionsFor(form.product.value) : []}
            value={form.productBatch}
            onChange={(v) => setForm(prev => ({ ...prev, productBatch: v || null }))}
            isDisabled={!form.product}
            placeholder={form.product ? "Select batch to deduct from..." : "Select product first"}
            isClearable isSearchable
            styles={selectStyles}
          />
          {form.productBatch && (
            <small className="text-warning d-block mt-1">
              ⚠️ This quantity will be permanently deducted from this batch.
            </small>
          )}
        </div>
      )}

      {!isEdit && (
        <div className="col-6">
          <label className="fs-4">Quantity <span className="text-danger">*</span></label>
          <input
            className="form-control form-control-lg border-2"
            type="number" min="1"
            placeholder="0"
            value={form.quantity}
            onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))}
          />
        </div>
      )}

      <div className={isEdit ? "col-12" : "col-6"}>
        <label className="fs-4">Type <span className="text-danger">*</span></label>
        <Select
          options={typeOptions}
          value={form.type}
          onChange={(v) => setForm(prev => ({ ...prev, type: v || null }))}
          isDisabled={isEdit}
          placeholder="Select type..."
          styles={selectStyles}
        />
      </div>

      <div className="col-12">
        <label className="fs-4">Reason <span className="text-danger">*</span></label>
        <textarea
          className="form-control border-2" rows={2}
          placeholder="Describe what happened..."
          value={form.reason}
          onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
        />
      </div>

      {!isEdit && (
        <div className="col-6">
          <SimpleDualDateInput
            label="Adjustment Date"
            value={form.adjustmentDate}
            onChange={(date) => setForm(prev => ({ ...prev, adjustmentDate: date }))}
            maxDate={new Date()}
          />
        </div>
      )}

      {/* Recoverable toggle */}
      <div className="col-12">
        <div className="form-check form-switch d-flex align-items-center gap-3 border rounded-3 px-4 py-3 bg-white">
          <input
            className="form-check-input" type="checkbox"
            id="isRecoverable" checked={form.isRecoverable}
            onChange={(e) => setForm(prev => ({ ...prev, isRecoverable: e.target.checked }))}
            style={{ transform: "scale(1.6)", margin: 0, cursor: "pointer" }}
          />
          <label className="form-check-label fw-medium mb-0" htmlFor="isRecoverable"
            style={{ fontSize: "1.5rem" }}>
            Recoverable (can be sold at reduced price)
          </label>
        </div>
      </div>

      {form.isRecoverable && (
        <div className="col-12">
          <label className="fs-4">Recovery Note</label>
          <input
            className="form-control form-control-lg border-2"
            placeholder="e.g. Minor chip — sell at 50% discount"
            value={form.recoveryNote}
            onChange={(e) => setForm(prev => ({ ...prev, recoveryNote: e.target.value }))}
          />
        </div>
      )}

    </div>
  );

  // ── Table columns ──────────────────────────────────────────
  const columns = useMemo(() => {
    const base = [
      { Header: 'No', accessor: 'no', Cell: ({ row }) => row.index + 1 },
      { Header: 'Product',  accessor: 'product.name',  Filter: SelectColumnFilter, filter: 'multiSelectFilter', Cell: ({ value }) => dash(value) },
      { Header: 'SKU',      accessor: 'product.sku',   Cell: ({ value }) => dash(value) },
      { Header: 'Type',     accessor: 'type',          Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value }) => {
          const colors = { lost: 'danger', damaged: 'warning', expired: 'secondary', correction: 'info', other: 'dark' };
          return <span className={`badge bg-${colors[value] || 'secondary'} rounded-pill px-2 py-1`}>{value}</span>;
        }
      },
      { Header: 'Quantity', accessor: 'quantity', Cell: ({ value }) => dash(value?.toFixed(3)) },
      {
        Header: 'Batch Stock Before',
        accessor: 'productBatch.remainingQuantity',
        Cell: ({ value, row }) => {
          // Show current remaining + what was deducted
          const deducted  = row.original.quantity || 0;
          const remaining = (value || 0);
          return `${remaining.toFixed(3)} (was ${ (remaining + deducted).toFixed(3) }).`;
        }
      },
      { Header: 'Reason',    accessor: 'reason',        Cell: ({ value }) => dash(value) },
      { Header: 'Recoverable', accessor: 'isRecoverable',
        Cell: ({ value }) => value
          ? <span className="badge bg-success rounded-pill px-2 py-1">Yes</span>
          : <span className="text-muted">No</span>
      },
      { Header: 'Adjusted By', accessor: 'adjustedBy.firstname', Filter: SelectColumnFilter, filter: 'multiSelectFilter', Cell: ({ value }) => dash(value) },
      { Header: 'Date',      accessor: 'adjustmentDate', Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"
      },
    ];

    if (isAdmin) {
      base.push({
        Header: 'Action',
        Cell: ({ row }) => (
          <>
            <span className="text-warning" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openEditModal(row.original._id)}>
              <MdEditSquare size={24} />
            </span>
            <span className="text-danger" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openDeleteModal(row.original._id)}
              title="Delete and restore stock">
              <MdDeleteForever size={24} />
            </span>
          </>
        )
      });
    }

    return base;
  }, [isAdmin]);

  const data = useMemo(() =>
  Array.isArray(adjustments)
    ? adjustments.map(a => ({ ...a })) // ✅ shallow clone to prevent react-table traversing nested objects
    : [],
[adjustments]);

  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns, data, filterTypes,
    defaultColumn: { Filter: () => null }
  }, useFilters, useGlobalFilter, usePagination);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow, allColumns, getToggleHideAllColumnsProps
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div>

      {/* ── ADD MODAL ─────────────────────────────────────────── */}
      <div className="modal fade modal-dialog-scrollable" ref={addModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg ">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fff3cd" }}>
                  <AiOutlinePlus color="#856404" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Record Stock Adjustment</h3>
                  <small className="text-muted fs-5">
                    Modal stays open — record multiple adjustments.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <form onSubmit={handleAddSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields(false)}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">Close</button>
                {isLoading ? (
                  <ButtonLoading className="btn btn-warning flex-fill rounded-3 fw-medium py-2 fs-2" disabled>
                    Saving...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-warning flex-fill rounded-3 fw-medium py-2 text-dark fs-2">
                    Record Adjustment
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ────────────────────────────────────────── */}
      <div className="modal fade modal-dialog-scrollable" ref={editModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <MdEditSquare color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Edit Adjustment</h3>
                  <small className="text-muted fs-5">Only reason and recovery notes can be updated.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <form onSubmit={handleEditSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields(true)}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">Cancel</button>
                <button type="submit"
                  className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── DELETE MODAL ──────────────────────────────────────── */}
      <div className="modal fade modal-dialog-scrollable" ref={deleteModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}>
                  <MdDeleteForever color="red" size={34} />
                </div>
                <div>
                  <h5 className="modal-title fw-semibold mb-0">Delete Adjustment</h5>
                  <small className="text-muted">Stock will be restored to the batch.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0">
                  Are you sure? The adjusted quantity will be added back to the original batch.
                </p>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 gap-2">
              <button className="btn btn-primary border flex-fill rounded-3 fw-medium py-2"
                data-bs-dismiss="modal">Cancel</button>
              <button className="btn btn-danger flex-fill rounded-3 fw-medium py-2"
                onClick={handleDelete}>
                <MdDeleteForever size={18} className="me-1" />Delete & Restore Stock
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── CONVERT STOCK MODAL ───────────────────────────────────── */}
    <div className="modal fade modal-dialog-scrollable" ref={convertModalRef} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content border-0 rounded-4 shadow-lg p-2">

          <div className="modal-header pb-3">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: 48, height: 48, backgroundColor: "#e8f5e9" }}>
                <AiOutlinePlus color="#198754" size={28} />
              </div>
              <div>
                <h3 className="modal-title fw-semibold mb-1">Convert Stock</h3>
                <small className="text-muted fs-5">
                  Convert one product into another — e.g. 60×60 ceramic → skirting.
                  Source stock is deducted, target stock is added automatically.
                </small>
              </div>
            </div>
            <button className="btn-close ms-auto" data-bs-dismiss="modal" />
          </div>

          <form onSubmit={handleConvertSubmit} className="border-0">
            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <div className="row g-3">

                  {/* ── SOURCE ────────────────────────────────────────── */}
                  <div className="col-12">
                    <p className="fw-semibold fs-4 border-bottom pb-2 mb-3 text-danger">
                      Source — Material Being Converted
                    </p>
                  </div>

                  {/* Source product */}
                  <div className="col-12 col-md-6">
                    <label className="fs-4">
                      Source Product <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={convertibleProductOptions}
                      value={convertForm.sourceProduct}
                      onChange={(v) => setConvertForm(() => ({
                        ...convertInitial,
                        sourceProduct: v || null,
                      }))}
                      placeholder="Select product to convert from..."
                      isClearable isSearchable
                      styles={selectStyles}
                    />
                    {sourceProductDoc && (
                      <small className="text-muted fs-5">
                        {sourceProductDoc.sizePerItem} m²/pc
                        {sourceProductDoc.itemsPerBox
                          ? ` · ${sourceProductDoc.itemsPerBox} pcs/box` : ""}
                      </small>
                    )}
                  </div>

                  {/* Source batch */}
                  <div className="col-12 col-md-6">
                    <label className="fs-4">
                      Source Batch <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={convertForm.sourceProduct
                        ? batchOptionsFor(convertForm.sourceProduct.value) : []}
                      value={convertForm.sourceBatch}
                      onChange={(v) => setConvertForm(prev => ({
                        ...prev, sourceBatch: v || null
                      }))}
                      isDisabled={!convertForm.sourceProduct}
                      placeholder={convertForm.sourceProduct
                        ? "Select batch..." : "Select product first"}
                      isClearable isSearchable
                      styles={selectStyles}
                    />
                  </div>

                  {/* Source quantity */}
                  <div className="col-6 col-md-3">
                    <label className="fs-4">
                      Qty to Convert <span className="text-danger">*</span>
                      <span className="text-muted ms-1 fs-5">
                        ({sourceUnit === "m2" ? "m²"
                          : sourceUnit === "linear_meter" ? "m" : "pcs"})
                      </span>
                    </label>
                    <input
                      className="form-control form-control-lg border-2"
                      type="number" min="0.001" step="0.001"
                      placeholder="0"
                      value={convertForm.sourceQuantity}
                      onChange={(e) => setConvertForm(prev => ({
                        ...prev, sourceQuantity: e.target.value
                      }))}
                    />

                    {/* ── Quantity calculator — same as sales and batch ── */}
                    {sourceBuyOptions && (
                      <div className="mt-2">
                        <div className="d-flex flex-wrap gap-2">

                          {/* ▲ Round up — recommended */}
                          <button type="button"
                            className={`btn btn-sm rounded-3 fs-5 ${
                              Number(convertForm.sourceQuantity) ===
                              sourceBuyOptions.up.quantityValue
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            onClick={() => setConvertForm(prev => ({
                              ...prev, sourceQuantity: sourceBuyOptions.up.quantityValue
                            }))}>
                            <span className="badge bg-success me-1 fs-6">Recommended</span>
                            ▲ {sourceBuyOptions.up.sizeLabel} — {sourceBuyOptions.up.pieces} pcs
                            {sourceBuyOptions.up.boxLabel && (
                              <span className="ms-1 opacity-75">
                                ({sourceBuyOptions.up.boxLabel})
                              </span>
                            )}
                          </button>

                          {/* ▼ Round down */}
                          {sourceBuyOptions.down && (
                            <button type="button"
                              className={`btn btn-sm rounded-3 fs-5 ${
                                Number(convertForm.sourceQuantity) ===
                                sourceBuyOptions.down.quantityValue
                                  ? "btn-warning text-dark"
                                  : "btn-outline-warning text-dark"
                              }`}
                              onClick={() => setConvertForm(prev => ({
                                ...prev, sourceQuantity: sourceBuyOptions.down.quantityValue
                              }))}>
                              ▼ {sourceBuyOptions.down.sizeLabel} — {sourceBuyOptions.down.pieces} pcs
                              {sourceBuyOptions.down.boxLabel && (
                                <span className="ms-1 opacity-75">
                                  ({sourceBuyOptions.down.boxLabel})
                                </span>
                              )}
                            </button>
                          )}

                        </div>

                        {/* Box info for selected source quantity */}
                        {sourceBoxInfo && (
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            <span className="badge bg-primary bg-opacity-25 text-primary
                                            rounded-pill px-3 py-1 fs-5">
                              {sourceBoxInfo.boxLabel}
                            </span>
                            <span className="badge bg-info bg-opacity-25 text-info
                                            rounded-pill px-3 py-1 fs-5">
                              {sourceBoxInfo.sizeLabel}
                            </span>
                            {sourceUnit !== "pcs" && sourceBoxInfo.totalPcs != null && (
                              <span className="badge bg-secondary bg-opacity-25 text-secondary
                                              rounded-pill px-3 py-1 fs-5">
                                {sourceBoxInfo.totalPcs} pcs total
                              </span>
                            )}
                          </div>
                        )}

                      </div>
                    )}
                  </div>

                  {/* Total m² display */}
                  {sourceTotalM2 > 0 && (
                    <div className="col-6 col-md-3 d-flex align-items-end">
                      <div className="w-100 p-3 bg-white border rounded-3">
                        <p className="text-muted mb-0 fs-5">Total Area</p>
                        <p className="fw-bold mb-0 fs-4 text-primary">
                          {sourceTotalM2.toFixed(4)} m²
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── SOURCE TILE DIMENSIONS ── */}
                  {sourceUnit === "m2" && convertForm.sourceQuantity && (
                    <div className="col-12">
                      <div className="border rounded-3 px-3 py-3 bg-white">
                        <p className="fw-semibold fs-4 mb-3 border-bottom pb-2">
                          Tile Dimensions
                          <span className="text-muted ms-2 fs-5 fw-normal">
                            — needed to calculate how many strips are produced
                          </span>
                        </p>
                        <div className="row g-3">

                          <div className="col-6 col-md-3">
                            <label className="fs-4">
                              Tile Width (m) <span className="text-danger">*</span>
                            </label>
                            <input
                              className="form-control form-control-lg border-2"
                              type="number" min="0.01" step="0.01"
                              placeholder="e.g. 0.60"
                              value={convertForm.sourceWidth}
                              onChange={(e) => setConvertForm(prev => ({
                                ...prev,
                                sourceWidth:  e.target.value,
                                cuttingSide:  "",
                              }))}
                            />
                            <div className="form-text fs-5">e.g. 0.60 for 60cm</div>
                          </div>

                          <div className="col-6 col-md-3">
                            <label className="fs-4">
                              Tile Length (m) <span className="text-danger">*</span>
                            </label>
                            <input
                              className="form-control form-control-lg border-2"
                              type="number" min="0.01" step="0.01"
                              placeholder="e.g. 0.60"
                              value={convertForm.sourceLength}
                              onChange={(e) => setConvertForm(prev => ({
                                ...prev,
                                sourceLength: e.target.value,
                                cuttingSide:  "",
                              }))}
                            />
                            <div className="form-text fs-5">e.g. 0.30 for 30cm</div>
                          </div>

                          {/* Cutting side selector */}
                          {convertForm.sourceWidth && convertForm.sourceLength && (
                            <div className="col-12 col-md-6">
                              <label className="fs-4">
                                Which side is being cut?{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <div className="d-flex gap-2 flex-wrap mt-1">
                                {sourceDimensions?.sides.map((side, i) => (
                                  <button key={i} type="button"
                                    className={`btn rounded-3 fs-4 ${
                                      Number(convertForm.cuttingSide) === side.value
                                        ? "btn-primary"
                                        : "btn-outline-primary"
                                    }`}
                                    onClick={() => setConvertForm(prev => ({
                                      ...prev, cuttingSide: String(side.value)
                                    }))}>
                                    {side.label}
                                  </button>
                                ))}
                              </div>
                              <div className="form-text fs-5">
                                The strips will be cut perpendicular to this side.
                                Strip length = the other dimension.
                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── TARGET ────────────────────────────────────────── */}
                  <div className="col-12">
                    <p className="fw-semibold fs-4 border-bottom pb-2 mb-3 mt-2 text-success">
                      Target — Product Being Created
                    </p>
                  </div>

                  {/* Target product */}
                  <div className="col-12 col-md-6">
                    <label className="fs-4">
                      Target Product <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={convertibleProductOptions.filter(
                        o => o.value !== convertForm.sourceProduct?.value
                      )}
                      value={convertForm.targetProduct}
                      onChange={(v) => setConvertForm(prev => ({
                        ...prev,
                        targetProduct:  v || null,
                        targetQuantity: "",
                        cuttingWidth:   "",
                      }))}
                      placeholder="Select product to convert into..."
                      isClearable isSearchable
                      styles={selectStyles}
                    />
                    {targetProductDoc && (
                      <small className="text-muted fs-5">
                        {targetProductDoc.sizePerItem}{" "}
                        {targetUnit === "m2" ? "m²"
                          : targetUnit === "linear_meter" ? "m" : "pcs"}/pc
                      </small>
                    )}
                  </div>

                  {/* Cutting width — for linear_meter targets */}
                  {targetUnit === "linear_meter" && convertForm.cuttingSide && (
                    <div className="col-6 col-md-3">
                      <label className="fs-4">
                        Strip Width (m) <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control form-control-lg border-2"
                        type="number" min="0.01" step="0.01"
                        placeholder="e.g. 0.10 for 10cm skirting"
                        value={convertForm.cuttingWidth}
                        onChange={(e) => setConvertForm(prev => ({
                          ...prev, cuttingWidth: e.target.value
                        }))}
                      />
                      <div className="form-text fs-5">
                        Width of each cut strip — max {convertForm.cuttingSide}m
                      </div>
                    </div>
                  )}

                  {/* Detailed calculation breakdown — unchanged */}
                  {conversionResult && (
                    <div className="col-12">
                      <div className="border rounded-3 px-3 py-3 bg-light">
                        <p className="fw-semibold fs-4 mb-2">Conversion Breakdown</p>
                        <div className="row g-2">
                          <div className="col-6 col-md-3">
                            <div className="p-2 bg-white border rounded-3 text-center">
                              <p className="text-muted mb-0 fs-5">Strips per tile</p>
                              <p className="fw-bold mb-0 fs-4">
                                {conversionResult.stripsPerTile}
                              </p>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="p-2 bg-white border rounded-3 text-center">
                              <p className="text-muted mb-0 fs-5">Strip length</p>
                              <p className="fw-bold mb-0 fs-4">
                                {conversionResult.stripLength}m
                              </p>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="p-2 bg-white border rounded-3 text-center">
                              <p className="text-muted mb-0 fs-5">Total tiles</p>
                              <p className="fw-bold mb-0 fs-4">
                                {conversionResult.totalTiles}
                              </p>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="p-2 bg-white border rounded-3 text-center">
                              <p className="text-muted mb-0 fs-5">Wastage</p>
                              <p className={`fw-bold mb-0 fs-4 ${
                                conversionResult.wastageM2 > 0
                                  ? "text-danger" : "text-success"
                              }`}>
                                {conversionResult.wastageM2 > 0
                                  ? `${conversionResult.wastageM2.toFixed(4)} m²`
                                  : "None"}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 p-2 bg-white border rounded-3">
                          <small className="text-muted fs-5 font-monospace">
                            {conversionResult.formula}
                          </small>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Target selling price */}
                  <div className="col-6 col-md-3">
                    <label className="fs-4">
                      Selling Price <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control form-control-lg border-2"
                      type="number" min="0"
                      placeholder="0.00"
                      value={convertForm.targetSellingPrice}
                      onChange={(e) => setConvertForm(prev => ({
                        ...prev, targetSellingPrice: e.target.value
                      }))}
                    />
                    <div className="form-text fs-5">
                      Per {targetUnit === "m2" ? "m²"
                        : targetUnit === "linear_meter" ? "meter" : "piece"}
                    </div>
                  </div>

                  {/* Target purchase price */}
                  <div className="col-6 col-md-3">
                    <label className="fs-4">Purchase Price</label>
                    <input
                      className="form-control form-control-lg border-2"
                      type="number" min="0"
                      placeholder="0.00 (optional)"
                      value={convertForm.targetPurchasePrice}
                      onChange={(e) => setConvertForm(prev => ({
                        ...prev, targetPurchasePrice: e.target.value
                      }))}
                    />
                  </div>

                  {/* Date */}
                  <div className="col-6">
                    <SimpleDualDateInput
                      label="Conversion Date"
                      value={convertForm.conversionDate}
                      onChange={(date) => setConvertForm(prev => ({
                        ...prev, conversionDate: date
                      }))}
                      maxDate={new Date()}
                    />
                      
                  </div>

                  {/* Supplier */}
                  <div className="col-6 col-md-3">
                    <label className="fs-4">Supplier / Note</label>
                    <input
                      className="form-control form-control-lg border-2"
                      placeholder="e.g. In-house cutting"
                      value={convertForm.targetSupplier}
                      onChange={(e) => setConvertForm(prev => ({
                        ...prev, targetSupplier: e.target.value
                      }))}
                    />
                  </div>

                  

                  {/* Reason */}
                  <div className="col-12">
                    <label className="fs-4">Reason / Notes</label>
                    <textarea
                      className="form-control border-2" rows={2}
                      placeholder="e.g. Cut 60×60 ceramic into 10×60 skirting for customer order"
                      value={convertForm.reason}
                      onChange={(e) => setConvertForm(prev => ({
                        ...prev, reason: e.target.value
                      }))}
                    />
                  </div>

                  {/* Summary before submit */}
                  {convertForm.sourceQuantity && convertForm.targetQuantity && (
                    <div className="col-12">
                      <div className="d-flex flex-wrap gap-3 p-3 bg-white border rounded-3">
                        <div>
                          <p className="text-muted mb-0 fs-5">FROM</p>
                          <p className="fw-bold mb-0 fs-4">
                            {convertForm.sourceQuantity}{" "}
                            {sourceUnit === "m2" ? "m²"
                              : sourceUnit === "linear_meter" ? "m" : "pcs"}{" "}
                            {sourceProductDoc?.name}
                          </p>
                        </div>
                        <div className="d-flex align-items-center fs-3 text-muted">→</div>
                        <div>
                          <p className="text-muted mb-0 fs-5">TO</p>
                          <p className="fw-bold mb-0 fs-4 text-success">
                            {convertForm.targetQuantity}{" "}
                            {targetUnit === "m2" ? "m²"
                              : targetUnit === "linear_meter" ? "m" : "pcs"}{" "}
                            {targetProductDoc?.name}
                          </p>
                        </div>
                        {convertForm.targetSellingPrice && convertForm.targetQuantity && (
                          <div>
                            <p className="text-muted mb-0 fs-5">New Batch Value</p>
                            <p className="fw-bold mb-0 fs-4 text-primary">
                              {(Number(convertForm.targetQuantity) *
                                Number(convertForm.targetSellingPrice)).toLocaleString()} birr
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 gap-2">
              <button type="button"
                className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                data-bs-dismiss="modal">Cancel</button>
              {isLoadingConvert ? (
                <ButtonLoading
                  className="btn btn-success flex-fill rounded-3 fw-medium py-2 fs-2"
                  disabled>
                  Converting...
                </ButtonLoading>
              ) : (
                <button type="submit"
                  className="btn btn-success flex-fill rounded-3 fw-medium py-2 fs-2">
                  Convert Stock
                </button>
              )}
            </div>
          </form>

        </div>
      </div>
    </div>

      {/* ── TABLE ─────────────────────────────────────────────── */}
     <div className="d-flex justify-content-between align-items-center
                m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0 fs-4">Stock Adjustments</h4>
          {/* ✅ Tab toggle */}
          <div className="btn-group btn-group-sm">
            <button
              className={`btn rounded-start-3 fs-4 fw-medium ${
                activeTab === "adjustments" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setActiveTab("adjustments")}>
              Adjustments
            </button>
            <button
              className={`btn rounded-end-3 fs-4 fw-medium ${
                activeTab === "conversions" ? "btn-success" : "btn-outline-success"
              }`}
              onClick={() => setActiveTab("conversions")}>
              Conversions
              {adjustments.filter(a => a.type === "conversion").length > 0 && (
                <span className="badge bg-success ms-2 rounded-pill fs-5">
                  {adjustments.filter(a => a.type === "conversion").length}
                </span>
              )}
            </button>
          </div>
        </div>
        {isAdmin && (
          <div className="d-flex gap-2">
            <button className="btn btn-warning rounded-3 text-dark"
              onClick={openAddModal}>
              <AiOutlinePlus className="me-1" /> Record Adjustment
            </button>
            <button className="btn btn-success rounded-3"
              onClick={openConvertModal}>
              ⇄ Convert Stock
            </button>
          </div>
        )}
      </div>
      {activeTab === "adjustments" ? (
        <>
      <div className="px-4 mb-4">
        <div className="d-flex align-items-center justify-content-between gap-3 px-4 rounded-4 border"
          style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
          <ColumnToggle allColumns={allColumns} getToggleHideAllColumnsProps={getToggleHideAllColumnsProps} />
        </div>
      </div>

      <div className="px-4">
        <div className="table-responsive rounded-4 border mt-5" style={{ backgroundColor: "#ffffff" }}>
          <table className="table table-hover table-striped fs-4"
            {...getTableProps()}
            style={{ minWidth: "100%", fontSize: "12px", margin: 0 }}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th className="px-4" style={{ backgroundColor: "#faf8f5" }}
                      {...column.getHeaderProps()}>
                      {column.render('Header')}
                      {column.canFilter && column.render('Filter')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td className="px-4" style={{ backgroundColor: "#ffffff" }}
                        {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {page.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">
                    No adjustments recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: '15px' }}>
            page <strong><em style={{ color: 'blue' }}>{pageIndex + 1}</em> of {pageOptions.length}</strong>
          </span>
          <select className="form-select form-select-lg" style={{ maxWidth: '90px' }}
            value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 20, 30, 50].map((s, i) => <option key={i} value={s}>Show {s}</option>)}
          </select>
          <li><button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button></li>
          <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
          </li>
          <li><button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button></li>
        </ul>
      </div>

      </>  ) : (
            <ConvertedItems adjustments={adjustments} />
          )}
    </div>
    
  );
};